import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { createSelector, Store } from '@ngrx/store';
import Color from 'color';
import { cloneDeep } from 'lodash';

import { Scopes } from '@ninety/platform/models/entitlements/entitlements-model';
import { MeetingSection } from '@ninety/platform/models/meetings/meeting-section';
import { MeetingType } from '@ninety/platform/models/meetings/meeting-type.enum';
import { StateService } from '@ninety/platform/services';
import {
  selectCompanyColorBranding,
  selectCompanyMasterySettings,
  selectDoesUserHaveAccessToOrgChart,
  selectFeatureEnabled,
  selectLanguage,
} from '@ninety/platform/state';
import { TerraDividerModule, TerraIconModule } from '@ninety/terra';
import { NinetyTooltipDirective } from '@ninety/ui/core';

import { MeetingService } from '../_shared/services/meeting.service';
import { MeetingsPageActions } from '../_state/meetings.actions';
import { MeetingsStateSelectors } from '../_state/meetings.selectors';

import { MeetingSidenavActionsComponent } from './meeting-sidenav-actions/meeting-sidenav-actions.component';
import {
  MeetingHeaderTimers,
  MeetingSidenavHeaderComponent,
} from './meeting-sidenav-header/meeting-sidenav-header.component';
import { MeetingSidenavSectionListComponent } from './meeting-sidenav-section-list/meeting-sidenav-section-list.component';
import { MeetingViewTool } from './meeting-sidenav-tools-menu/meeting-sidenav-tools-menu.component';

/**
 * Is the company's brand color light enough that white-on-brand would be unreadable?
 *
 * SCSS can't answer this: `terra.color('brand')` is `var(--brand)`, which only exists at runtime.
 * So we branch here on the same `--primaryAccent` hex the store holds and expose the answer as a
 * class the stylesheet switches `text` / `text-invert` on. Defaults to "not light" (white chevron).
 */
const selectBrandColorIsLight = createSelector(selectCompanyColorBranding, theme => {
  const hex = theme?.properties?.['--primaryAccent'];
  if (!hex) return false;

  try {
    return Color(hex).isLight();
  } catch {
    return false;
  }
});

const selectMeetingItemLabel = createSelector(selectLanguage, language => language?.meeting?.item);

@Component({
  standalone: true,
  selector: 'ninety-meeting-sidenav-v2',
  templateUrl: './meeting-sidenav-v2.component.html',
  styleUrls: ['./meeting-sidenav-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TerraIconModule,
    TerraDividerModule,
    NinetyTooltipDirective,
    MeetingSidenavHeaderComponent,
    MeetingSidenavSectionListComponent,
    MeetingSidenavActionsComponent,
  ],
})
export class MeetingSidenavV2Component {
  // --- Dependencies ------------------------------------------------------------------------

  private readonly store = inject(Store);
  private readonly stateService = inject(StateService);
  private readonly meetingService = inject(MeetingService);

  // --- Host inputs -------------------------------------------------------------------------

  readonly showNav = input(false);
  readonly isMobile = input(false);
  /** Host-owned; fed from `MeetingService.showNotes$`. Drives the notes button's label. */
  readonly showNotes = input(false);

  // --- Shared state (used by more than one band) -------------------------------------------

  // Read the meeting from the store selector, not MeetingService.currentMeeting$ (deprecated).
  private readonly currentMeeting = this.store.selectSignal(MeetingsStateSelectors.selectCurrentMeeting);

  /** Presenter-only bands: timers, meeting controls, Finish. Attendees get Leave instead. */
  protected readonly isPresenter = this.store.selectSignal(MeetingsStateSelectors.selectIsPresenter);

  /** @see {@link selectMeetingItemLabel} — label for the attendee Leave button. */
  protected readonly meetingItemLabel = this.store.selectSignal(selectMeetingItemLabel);

  // --- Shell: rail visibility + collapse toggle ---------------------------------------------

  /** Show the rail once a meeting has loaded (control used a one-shot latch; here it's derived). */
  protected readonly showSidebar = computed(() => !!this.currentMeeting());

  /** @see {@link selectBrandColorIsLight} — picks the chevron color for the branded toggle. */
  protected readonly brandIsLight = this.store.selectSignal(selectBrandColorIsLight);

  private readonly isFinishing = this.store.selectSignal(MeetingsStateSelectors.selectIsFinishing);

  toggleMeetingNav(): void {
    const expanded = !this.showNav();
    this.stateService.toggleMeetingSideNav(expanded);
    // Analytics: MeetingsAnalyticsEffects maps this to the Segment `meeting_navigation_toggled` event.
    this.store.dispatch(MeetingsPageActions.meetingNavToggled({ expanded }));
  }

  // --- Timer band: total, progress, section clock (presenter only) --------------------------

  // Timer state still lives on the frozen MeetingService, which pushes to these subjects on each
  // tick. V2 reads them as signals rather than re-deriving the tick loop here.
  private readonly meetingTime = toSignal(this.meetingService.meetingTimer$, { initialValue: 0 });
  protected readonly sectionTime = toSignal(this.meetingService.sectionTimer$, { initialValue: 0 });
  private readonly progressBarValue = toSignal(this.meetingService.progressBarValue$, { initialValue: 0 });
  private readonly navigationEnded = toSignal(this.meetingService.navigationEnded$, { initialValue: false });

  /**
   * Section length in seconds. Read from the store meeting, not the service's `currentSectionDuration`:
   * duration changes only on a section change, and `changeMeetingSection` dispatches the new
   * `currentSection` to the store — so the store value is never stale. Avoids both the frozen-service
   * read and the old "read `sectionTime()` only to force a recompute" invalidation hack.
   * `currentSection?` is defensive: the meeting can be present before section is set.
   */
  private readonly sectionDuration = computed(() => this.currentMeeting()?.currentSection?.duration ?? 0);

  /**
   * Presenter timer values bundled for the header. These all tick together each second, so grouping
   * them costs nothing reactively and keeps the header's input surface small.
   */
  protected readonly timers = computed<MeetingHeaderTimers>(() => ({
    meetingTime: this.meetingTime(),
    sectionTime: this.sectionTime(),
    sectionDuration: this.sectionDuration(),
    progressBarValue: this.progressBarValue(),
    navigationEnded: this.navigationEnded(),
  }));

  // --- Meeting controls: skip, pause/resume, suspend (presenter only) -----------------------

  /**
   * Reading `paused` off the store meeting is safe *for the presenter*, who is the only user shown
   * these controls: their own toggle goes through `updateCurrentMeeting`, which re-dispatches the
   * meeting to the store, so this signal changes on the same tick as the click. (A remote
   * Pause/Continue mutates the service's meeting in place without a dispatch — that would be stale
   * here, but the emitter ignores its own broadcast and attendees never see this button.)
   */
  protected readonly isPaused = computed(() => !!this.currentMeeting()?.paused);

  /** Suspend is one-shot: the button stays disabled while the meeting is being torn down. */
  protected readonly suspending = signal(false);

  onPauseToggled(): void {
    this.meetingService.toggleTimers();

    // The service mutates its own meeting in place, so read the payload from there rather than from
    // the store signal — elapsed times tick without a dispatch and would be stale here.
    const meeting = this.meetingService.currentMeeting;

    // `persistMeetingPause$` writes the new state (so a refresh resumes correctly rather than
    // restarting the timers) and `broadcastMeetingPause$` tells the other clients. Both live in
    // effects so neither is cancelled if the sidenav is destroyed mid-flight.
    // `currentSection` is cloned because NgRx deep-freezes action payloads in dev and this is the
    // service's *live* object — freezing it would make the 1s timer's `elapsedTime +=` throw on
    // every tick. `sections` isn't in the payload at all: `persistMeetingPause$` persists the
    // service's own array, since assigning any other one detaches `currentSection` from it and
    // stalls the total timer.
    this.store.dispatch(
      MeetingsPageActions.meetingPauseToggled({
        paused: meeting.paused,
        elapsedTime: meeting.elapsedTime,
        currentSection: cloneDeep(meeting.currentSection),
      })
    );
  }

  /** Skip to the adjacent section by ordinal. No-ops at either end of the agenda. */
  skipSection(next: boolean): void {
    const meeting = this.meetingService.currentMeeting;
    const targetOrdinal = meeting.currentSection.ordinal + (next ? 1 : -1);
    const section = meeting.sections.find((s: MeetingSection) => s.ordinal === targetOrdinal);
    if (!section) return;

    this.goToSection(section, targetOrdinal);
  }

  suspend(): void {
    this.suspending.set(true);
    this.meetingService.suspendCurrentMeeting();
  }

  /**
   * Moves the meeting to `section`. Shared with the section list so both paths behave identically —
   * notably the `change-stage` broadcast, which v1 omitted on the skip buttons, leaving other
   * clients on the previous section until the next message.
   */
  private goToSection(section: MeetingSection, index: number): void {
    const meeting = this.meetingService.currentMeeting;

    // Resolve to the service's OWN section instance. The list renders store copies, but the service
    // ticks `elapsedTime` on its own objects, and `changeMeetingSection` assigns whatever it is
    // given to `currentMeeting.currentSection`. Hand it a copy and `currentSection` detaches from
    // the ticking one, so the next `change-stage` broadcast reports a frozen `stageElapsedTime` and
    // attendees never receive the section's real time. v1 passed the live object by construction.
    const liveSection = meeting.sections.find(s => s.ordinal === section.ordinal) ?? section;

    // Dispatched before the move so the elapsed times describe the section being left.
    // `broadcastSectionChange$` owns the presenter gate and the message itself.
    this.store.dispatch(
      MeetingsPageActions.meetingSectionChanged({
        stage: section.name,
        previousStage: meeting.currentSection.name,
        meetingElapsedTime: meeting.elapsedTime,
        stageElapsedTime: meeting.currentSection.elapsedTime,
        stageOrdinal: section.ordinal,
      })
    );

    this.meetingService.navigate(this.meetingService.getMeetingRoute(liveSection));
    this.meetingService.changeMeetingSection(liveSection, index);
  }

  // --- Leave (non-presenter) ----------------------------------------------------------------

  /** Attendee leaves the meeting: client-only teardown, no finish side effects (that's Suspend). */
  exit(): void {
    this.meetingService.clearMeeting();
    this.meetingService.navigateToMeetingsHome();
  }

  // --- Section list (presenter + attendee) --------------------------------------------------

  protected readonly sections = computed(() => this.currentMeeting()?.sections ?? []);
  /** `?? null` — the list's input is `MeetingSection | null`, but `currentSection` on the store
   *  meeting is a required field, so the only `undefined` here comes from `currentMeeting` itself. */
  protected readonly currentSection = computed(() => this.currentMeeting()?.currentSection ?? null);

  /**
   * Routes the list's click through the same path as the skip buttons (`goToSection`) so both
   * entry points broadcast `change-stage` identically — see {@link goToSection}.
   */
  onSectionSelected({ section, index }: { section: MeetingSection; index: number }): void {
    this.goToSection(section, index);
  }

  // --- Autofollow: where the facilitator is, and whether we've drifted off it -----------------

  /** @see {@link MeetingsStateSelectors.selectPresenterFollowViewState} — all null when the flag is off. */
  protected readonly presenterFollow = this.store.selectSignal(MeetingsStateSelectors.selectPresenterFollowViewState);

  /** Navigation lives in `FollowPresenterEffects` — the sidenav only reports the click. */
  onFollowPresenterSelected(): void {
    this.store.dispatch(MeetingsPageActions.followPresenterClicked());
  }

  // --- Bottom actions: tangent, notes (everyone) + finish (presenter only) -------------------

  // --- View tools menu (presenter + attendee, items gated) -----------------------------------

  private readonly language = this.store.selectSignal(selectLanguage);
  private readonly canAccessOrgChart = this.store.selectSignal(selectDoesUserHaveAccessToOrgChart);
  private readonly canAccessInsights = this.store.selectSignal(selectFeatureEnabled(Scopes.insights));
  private readonly masterySettings = this.store.selectSignal(selectCompanyMasterySettings);

  /** Safe: the whole sidenav is behind `@if (showSidebar())`, which requires a loaded meeting. */
  protected readonly meetingId = computed(() => this.currentMeeting()!._id);

  protected readonly tools = computed<MeetingViewTool[]>(() => {
    const language = this.language();
    if (!language) return [];

    return [
      { segment: 'data', label: language?.scorecard?.route, icon: 'data' },
      { segment: 'rocks', label: language?.rock?.route, icon: 'rocks' },
      { segment: 'todos', label: language?.todo?.route, icon: 'to-dos' },
      { segment: 'issues', label: language?.issue?.route, icon: 'issues' },
      { segment: 'headlines', label: language?.headline?.items, icon: 'headlines' },
      { segment: 'vision', label: language?.vto?.route, icon: 'vision' },
      ...(this.canAccessOrgChart()
        ? [{ segment: 'chart', label: language?.acChart?.route, icon: 'responsibilities' as const }]
        : []),
      { segment: 'directory', label: language?.directory?.route, icon: 'directory' },
      ...(this.masterySettings()?.enabled
        ? [{ segment: 'mastery', label: language?.mastery?.route, icon: 'mastery' as const }]
        : []),
      { segment: ['knowledge', 'library'], label: 'Knowledge', icon: 'student' },
      ...(this.canAccessInsights() ? [{ segment: 'insights', label: 'Insights', icon: 'lightbulb' as const }] : []),
    ];
  });

  /**
   * Sets the *browser page title* — despite the control naming this handler `changeMeetingTitle`,
   * it never touched the meeting's own name. Navigation itself is the menu's `routerLink`.
   */
  onToolSelected(label: string): void {
    if (label) this.meetingService.setMeetingTitle(label);
  }

  /** `Day 1` / `Day 2` suffix on Finish for annual meetings; empty for every other type. */
  protected readonly finishDayLabel = computed(() => {
    switch (this.currentMeeting()?.type) {
      case MeetingType.annualDayOne:
        return 'Day 1';
      case MeetingType.annualDayTwo:
        return 'Day 2';
      default:
        return '';
    }
  });

  protected canFinish(): boolean {
    if (this.isFinishing()) return false;
    if (this.currentMeeting()?.type !== MeetingType.weekly) return true;
    return this.meetingService.validRatings();
  }

  onTangentClicked(): void {
    this.store.dispatch(MeetingsPageActions.tangentButtonClicked());
  }

  onNotesToggled(): void {
    this.meetingService.toggleShowNotes();
    this.store.dispatch(MeetingsPageActions.toggleNotesPanel({ visible: !this.showNotes() }));
  }

  saveAndExit(): void {
    const meeting = this.meetingService.currentMeeting;
    if (!meeting) return;

    const allRated = meeting.userRatings.every((rating, i) => !!rating.rating || meeting.absences[i]);
    this.meetingService.finishMeeting(!(meeting.type === MeetingType.annualDayOne || allRated));
  }
}
